import { styled } from '@abyss/web/tools/styled';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { MediaQuery } from '@abyss/web/ui/MediaQuery';
import { Text } from '@abyss/web/ui/Text';
import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import { ContentWidthWrapper } from '@pass-frontend/abyss/common/components/content-width-wrapper/ui';
import { MEDIA_QUERY } from '@pass-frontend/abyss/utility/style-helpers';
import {
  logAnalyticsEvent,
  EventType,
  getStandAlonePageName,
  PageTags,
} from '@pass-frontend/common/analytics';
import { useIntl } from '@pass-frontend/common/i18n';

enum IMAGE_POSITION {
  LEFT,
  RIGHT,
}

const HEADING_CSS = {
  'abyss-heading-root': {
    marginBottom: '32px',

    [MEDIA_QUERY.desktop]: {
      marginBottom: '64px',
    },
  },
};

const StyledWrapper = styled(Flex, {
  padding: '$lg 0',

  [MEDIA_QUERY.desktop]: {
    padding: '96px 0',
  },
});

const StyledInnerWrapper = styled(Flex, {
  width: '840px',
});

const StyledBenefitsListItem = styled('li', {
  marginBottom: '32px',

  [MEDIA_QUERY.desktop]: {
    marginBottom: '40px',
  },
});

const StyledDescription = styled(Text, {
  [MEDIA_QUERY.mobileAndTablet]: {
    textAlign: 'center',
  },
});

const StyledMobileImage = styled('img', {
  height: '184px',
});

const BENEFIT_IMAGE_CSS = {
  maxWidth: '300px',
};

const StyledLink = styled('a', {
  fontWeight: '$bold',
});

export function BenefitsOverviewLegacy() {
  const { t } = useIntl();
  const router = useRouter();

  const items = [
    {
      title: t('benefitsOverview.atTheGymTitle'),
      description: t('benefitsOverview.atTheGymDescription'),
      imageUrl: 'images/hot-pink-kettlebell-man.jpg',
      position: IMAGE_POSITION.LEFT,
    },
    {
      title: t('benefitsOverview.atFamilyMembershipTitle'),
      description: t('benefitsOverview.atFamilyMembershipDescription'),
      imageUrl: 'images/teal-basketball.jpg',
      position: IMAGE_POSITION.RIGHT,
    },
    {
      title: t('benefitsOverview.withGroceryDeliveryTitle'),
      description: t('benefitsOverview.withGroceryDeliveryDescription'),
      imageUrl: 'images/fuel-your-life-purple-grocery.jpg',
      position: IMAGE_POSITION.LEFT,
    },
  ];

  const handleFindGymNearMeClick = (event: any) => {
    event.preventDefault();
    logAnalyticsEvent(
      EventType.PageNav,
      {
        pageName: getStandAlonePageName(router?.pathname),
        pageTags: { [PageTags.NewPageLoad]: false },
      },
      event.nativeEvent,
    );
    router?.push('#gym-search-link');
  };

  return (
    <ContentWidthWrapper>
      <StyledWrapper justify="center">
        <div>
          <Heading offset={1} textAlign="center" css={HEADING_CSS}>
            {t('benefitsOverview.heading')}
          </Heading>
          <ul>
            {items.map(({ title, description, imageUrl, position }, i) => {
              const isImageFirst = position === IMAGE_POSITION.LEFT;
              const imageEl = (
                <img src={imageUrl} style={BENEFIT_IMAGE_CSS} alt={title} />
              );

              const textContent = (
                <>
                  <Heading offset={2}>{title}</Heading>
                  <StyledDescription color="$gray7">
                    {description}
                  </StyledDescription>
                  {title === t('benefitsOverview.atTheGymTitle') && (
                    <Link href="#gym-search-link" legacyBehavior>
                      <StyledLink
                        data-ui-element-name="find-a-gym-near-you"
                        onClick={handleFindGymNearMeClick}
                      >
                        {t('benefitsOverview.atTheGymFindAGymNearYouLink')}
                      </StyledLink>
                    </Link>
                  )}
                </>
              );

              return (
                <StyledBenefitsListItem key={i}>
                  <MediaQuery smallerThan="$lg">
                    <Flex
                      alignItems="center"
                      direction="column"
                      css={{ gap: '$md' }}
                    >
                      <StyledMobileImage src={imageUrl} alt={title} />
                      {textContent}
                    </Flex>
                  </MediaQuery>
                  <MediaQuery largerThan="$lg">
                    <StyledInnerWrapper
                      alignItems="center"
                      justify="space-between"
                    >
                      {isImageFirst && imageEl}
                      <Flex css={{ maxWidth: '408px', gap: '$md' }}>
                        {textContent}
                      </Flex>
                      {!isImageFirst && imageEl}
                    </StyledInnerWrapper>
                  </MediaQuery>
                </StyledBenefitsListItem>
              );
            })}
          </ul>
        </div>
      </StyledWrapper>
    </ContentWidthWrapper>
  );
}
